/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import { formatLastActive } from "../../utils/utils";
import ParticipationRate from "../ParticipationRate";
import UserSegmentToggle from "../common/UserSegmentToggle";
import { useUserSegment, watchFilterValue } from "../../context/UserSegmentContext";
import ExportButton from "../active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "../active-users/DateRangeBar";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import PauseSummaryCards from "./PauseSummaryCards";
import PauseTrendChart from "./PauseTrendChart";
import PauseContentMix from "./PauseContentMix";
import PauseEngagementBuckets from "./PauseEngagementBuckets";
import PausePlanCorrelation from "./PausePlanCorrelation";
import "../active-users/active-users.css";
import "./PauseDecisionTool.css";

const LIMIT = 20;
const STATE_KEY = "pauseListState";
const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' }, { key: 'email', label: 'Email' },
  { key: 'country', label: 'Country' }, { key: 'mobile_app_version', label: 'App Version' },
  { key: 'subscription_status', label: 'Subscription' }, { key: 'pause_count', label: 'Meditation' },
  { key: 'breath_count', label: 'Breath' }, { key: 'watch_count', label: 'Watch' },
  { key: 'mobile_count', label: 'Mobile' }, { key: 'other_count', label: 'Other' },
  { key: 'latest_meditation_time', label: 'Last Meditation (UTC)' }, { key: 'latest_breath_time', label: 'Last Breath (UTC)' },
];

const UserPauseCount = () => {
  const navigate = useNavigate();
  const { segment } = useUserSegment();               // global watch/without/all (feature 023)
  const watchFilter = watchFilterValue(segment) || 'all'; // 'with_watch' | 'without_watch' | 'all'

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [users, setData] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalUserBase, setTotalUserBase] = useState(0);
  const [splitCounts, setSplitCounts] = useState({});
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const [summary, setSummary] = useState({ range: {}, prior: {}, deltas: {} });
  const [trend, setTrend] = useState([]);
  const [contentMix, setContentMix] = useState({ meditation: [], breathing: [] });
  const [engagement, setEngagement] = useState({ buckets: [], total_pausing_users: 0, paused_once_never_returned: 0 });
  const [planCorrelation, setPlanCorrelation] = useState([]);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // Restore list state on return from a drill-down (US8 / FR-023).
    let restored = false;
    try {
      const saved = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
      if (saved) {
        restored = true;
        setFromDate(saved.fromDate || ''); setToDate(saved.toDate || '');
        setDisplayFromDate(saved.fromDate ? new Date(saved.fromDate) : null);
        setDisplayToDate(saved.toDate ? new Date(saved.toDate) : null);
        setSearchValue(saved.searchValue || ''); setSortField(saved.sortField || '');
        setSortOrder(saved.sortOrder || ''); setPage(saved.page || 1);
      }
    } catch { /* ignore */ }
    if (!restored) {
      // Default to the last 30 days (matches /unique-users-count) so the date
      // filter is always a concrete window, not a silent all-time query.
      const { fromStr, toStr } = presetRange('last30', new Date());
      setFromDate(fromStr); setToDate(toStr);
      setDisplayFromDate(new Date(fromStr)); setDisplayToDate(new Date(toStr));
      setPreset('last30');
    }
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => {
    if (token) loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder);
  }, [token]);

  // Re-fetch when the global segment changes (skip initial mount).
  const didMountSegment = useRef(false);
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    if (!token) return;
    setPage(1);
    loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder);
  }, [segment]);

  useEffect(() => {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({ fromDate, toDate, searchValue, sortField, sortOrder, page }));
  }, [fromDate, toDate, searchValue, sortField, sortOrder, page]);

  // Emails carry '+' and '&' — encode the term or the query string eats them.
  const enc = (s) => encodeURIComponent(s || '');
  const qs = (p, f, t, s, sf, so) =>
    `page=${p}&limit=${LIMIT}&from_date=${f}&to_date=${t}&search=${enc(s)}&sortField=${sf}&sortOrder=${so}&watch_filter=${watchFilter}`;
  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const getList = async (p, f, t, s, sf, so) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/user-pauses?${qs(p, f, t, s, sf, so)}`, auth());
      applyList(res.data);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const loadAll = async (p, f, t, s, sf, so) => {
    setLoading(true); setError('');
    const range = `from_date=${f}&to_date=${t}&watch_filter=${watchFilter}`;
    try {
      const [list, sum, tr, cm, eng, plan] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/user-pauses?${qs(p, f, t, s, sf, so)}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/pause-summary?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/pause-trend?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/pause-content-mix?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/pause-engagement?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/pause-plan-correlation?${range}`, auth()),
      ]);
      applyList(list.data);
      setSummary(sum.data.data || { range: {}, prior: {}, deltas: {} });
      setTrend(tr.data.data || []);
      setContentMix(cm.data.data || { meditation: [], breathing: [] });
      setEngagement(eng.data.data || { buckets: [], total_pausing_users: 0, paused_once_never_returned: 0 });
      setPlanCorrelation(plan.data.data || []);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const applyList = (d) => {
    setData(d.data || []);
    setTotalItem(d.totalItem || 0);
    setTotalUsers(d.userCount?.[0]?.user_count || 0);
    setTotalUserBase(d.totalUserCount || 0);
    setSplitCounts(d.splitUserCounts || {});
  };
  const handleErr = (err) => {
    if (err?.response?.status === 401) setError('auth');
    else setError('load');
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    getList(pageNumber, fromDate, toDate, searchValue, sortField, sortOrder);
  };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    const { fromStr: fs, toStr: ts } = presetRange(key, new Date());
    setPreset(key); setFromDate(fromStr || fs); setToDate(toStr || ts);
    setDisplayFromDate(new Date(fromStr || fs)); setDisplayToDate(new Date(toStr || ts));
    setPage(1);
    loadAll(1, fromStr || fs, toStr || ts, searchValue, sortField, sortOrder);
  };
  const handleSearch = () => { setPage(1); loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null);
    setSearchValue(''); setPreset(null); setPage(1);
    loadAll(1, '', '', '', sortField, sortOrder);
  };
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field); setSortOrder(order); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, field, order);
  };
  const openUser = (id) => navigate(`/pauses-list/${btoa(id)}`);

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/pause-export?from_date=${fromDate}&to_date=${toDate}&search=${enc(searchValue)}&sortField=${sortField}&sortOrder=${sortOrder}&watch_filter=${watchFilter}`,
      auth());
    return res.data.data || [];
  };

  const SortTh = ({ field, label }) => (
    <th className="au-th-sort" onClick={() => handleSort(field)}>
      {label} {sortField === field ? (sortOrder === 'ASC' ? '🔼' : '🔽') : ''}
    </th>
  );
  const hasRange = !!(fromDate || toDate || searchValue);
  const n = (v) => Number(v || 0).toLocaleString();
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Activity › <b>Pauses</b></span>
            <span className="au-route">/user-pause-count</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Pauses</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the pauses data. Please try again." onRetry={() => loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder)} />
          ) : (
            <>
              {/* Summary before detail */}
              <PauseSummaryCards totalUserBase={totalUserBase} pausedUsers={totalUsers} splitCounts={splitCounts} deltas={summary.deltas} />

              {/* Device splits (preserved) */}
              <div className="au-cards" data-testid="pause-device-cards">
                <div className="au-card"><span className="au-card__label">Total Watch Count</span><span className="au-card__value tnum">{n(users?.[0]?.total_watch_count)}</span><ParticipationRate className="au-card__sub" count={splitCounts.watch_user_count} total={totalUserBase} /></div>
                <div className="au-card"><span className="au-card__label">Total Mobile Count</span><span className="au-card__value tnum">{n(users?.[0]?.total_mobile_count)}</span><ParticipationRate className="au-card__sub" count={splitCounts.mobile_user_count} total={totalUserBase} /></div>
                <div className="au-card"><span className="au-card__label">Total Other Count</span><span className="au-card__value tnum">{n(users?.[0]?.total_other_count)}</span><ParticipationRate className="au-card__sub" count={splitCounts.other_user_count} total={totalUserBase} /></div>
              </div>

              <PauseTrendChart data={trend} />
              <PauseContentMix data={contentMix} />
              <PauseEngagementBuckets data={engagement} />
              <PausePlanCorrelation data={planCorrelation} />

              {/* Filters */}
              <DateRangeBar
                leading={(
                  <input className="au-input" type="text" placeholder="Search by name or email" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} aria-label="Search users by name or email" />
                )}
                preset={preset}
                displayFrom={displayFromDate}
                displayTo={displayToDate}
                hasRange={hasRange}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="pause-range-note">
                The date range filters pauses by their <b>activity date (UTC)</b> — the moment each meditation/breath was recorded (shown per user as “Last Meditation / Last Breath”).{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all pauses (no date filter).'}
              </p>

              {/* Detail table */}
              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : users.length === 0 ? (
                <EmptyState title="No pauses in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange ? 'No users match the current filters.' : 'There are no pauses to show for this view yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Per-user pauses</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="pauses.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table au-mau-table" data-testid="pause-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Email</th><th>Country</th><th>App Version</th><th>Subscription</th>
                          <SortTh field="pause_count" label="Meditation" />
                          <SortTh field="breath_count" label="Breath" />
                          <SortTh field="watch_count" label="Watch" />
                          <SortTh field="mobile_count" label="Mobile" />
                          <SortTh field="other_count" label="Other" />
                          <SortTh field="latest_meditation_time" label="Last Meditation (UTC)" />
                          <SortTh field="latest_breath_time" label="Last Breath (UTC)" />
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user, index) => (
                          <tr key={user.id} className="pause-row--click" tabIndex={0} role="link"
                            aria-label={`Open pause history for ${user.name}`}
                            onClick={() => openUser(user.id)}
                            onKeyDown={(e) => { if (e.key === 'Enter') openUser(user.id); }}>
                            <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Name" className="au-ellipsis">{user.name}</td>
                            <td data-label="Email" className="au-ellipsis">{user.email}</td>
                            <td data-label="Country">{user.country}</td>
                            <td data-label="App Version">{user.mobile_app_version}</td>
                            <td data-label="Subscription">{user.subscription_status}</td>
                            <td data-label="Meditation" className="tnum"><Link className="au-link" onClick={(e) => e.stopPropagation()} to={`/pauses-list/${btoa(user.id)}`}>{user.pause_count}</Link></td>
                            <td data-label="Breath" className="tnum"><Link className="au-link" onClick={(e) => e.stopPropagation()} to={`/breath-list/${btoa(user.id)}`}>{user.breath_count}</Link></td>
                            <td data-label="Watch" className="tnum">{user.watch_count || 0}</td>
                            <td data-label="Mobile" className="tnum">{user.mobile_count || 0}</td>
                            <td data-label="Other" className="tnum">{user.other_count || 0}</td>
                            <td data-label="Last Meditation (UTC)">{user.latest_meditation_time ? formatLastActive(user.latest_meditation_time) : '-'}</td>
                            <td data-label="Last Breath (UTC)">{user.latest_breath_time ? formatLastActive(user.latest_breath_time) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPauseCount;
